import React, { useEffect, useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";
import api from "../api";
import EquipmentManageDialog from "../components/EquipmentManageDialog";

function Card({ children, className = "" }) {
  return <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6 ${className}`}>{children}</div>;
}

const EQUIP_STATUSES = ["Pending Contact", "Contacted", "Order Processing", "Ordered", "Ready/Shipped", "Completed", "Cancelled"];
const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

function EquipmentTab() {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [link, setLink] = useState({ label: "", url: "" });
  const [notifyEmail, setNotifyEmail] = useState("");
  const [savingLink, setSavingLink] = useState(false);
  const [manage, setManage] = useState(null);
  const load = () => api.get("/admin/equipment").then((r) => setRows(r.data)).catch(() => {});
  const loadLink = () => api.get("/equipment/config").then((r) => { setLink(r.data.link || { label: "", url: "" }); setNotifyEmail(r.data.notify_email || ""); }).catch(() => {});
  useEffect(() => { load(); loadLink(); }, []);
  const saveLink = async () => {
    setSavingLink(true);
    try { const r = await api.put("/admin/equipment/config/link", { ...link, notify_email: notifyEmail }); setLink(r.data.link || { label: "", url: "" }); setNotifyEmail(r.data.notify_email || ""); toast({ title: "Settings saved", description: "Link and notification email updated." }); }
    catch (e) { toast({ title: "Save failed", description: e.response?.data?.detail || "", variant: "destructive" }); }
    finally { setSavingLink(false); }
  };
  return (
    <div className="space-y-4">
      <Card data-testid="admin-equipment-link">
        <h3 className="text-sm font-bold text-slate-900">Link above the request form</h3>
        <p className="text-sm text-slate-500">Add a link (e.g. a vendor order page or info doc) shown to applicants above the Equipment Request form. Leave the URL blank to hide it.</p>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1.5fr_auto]">
          <div className="space-y-1.5"><Label>Link label</Label><Input value={link.label} onChange={(e) => setLink({ ...link, label: e.target.value })} placeholder="Order equipment here" data-testid="equip-link-label" /></div>
          <div className="space-y-1.5"><Label>Link URL</Label><Input value={link.url} onChange={(e) => setLink({ ...link, url: e.target.value })} placeholder="https://…" data-testid="equip-link-url" /></div>
          <div className="flex items-end"><Button onClick={saveLink} disabled={savingLink} data-testid="save-equip-link-btn" className="btn-primary-gradient border-0 text-white">{savingLink ? "Saving…" : "Save settings"}</Button></div>
        </div>
        <div className="mt-3 space-y-1.5">
          <Label>Team notification email</Label>
          <Input value={notifyEmail} onChange={(e) => setNotifyEmail(e.target.value)} placeholder="team@campbellconnect.com" data-testid="equip-notify-email" />
          <p className="text-xs text-slate-400">We'll email this address the moment an applicant submits an equipment request. Leave blank to turn off.</p>
        </div>
      </Card>
      <Card data-testid="admin-equipment">
      <h3 className="text-sm font-bold text-slate-900">Equipment requests</h3>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-400">
            <th className="py-2 pr-4">Applicant</th><th className="py-2 pr-4">Equipment</th><th className="py-2 pr-4">Price</th><th className="py-2 pr-4">Shipping</th><th className="py-2 pr-4">Requested</th><th className="py-2 pr-4">Status</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-50 align-top" data-testid={`admin-equip-row-${r.id}`}>
                <td className="py-3 pr-4"><div className="font-medium text-slate-800">{r.applicant_name}</div><div className="text-xs text-slate-400">{r.email}</div><div className="text-xs text-slate-400">{r.phone}</div></td>
                <td className="py-3 pr-4 text-slate-600">{r.equipment_type === "complete_setup" ? "Complete Setup" : (r.selected_items || []).map((i) => i.label).join(", ")}</td>
                <td className="py-3 pr-4 text-slate-600">${r.equipment_type === "complete_setup" ? r.package_price : r.regular_total}{r.savings ? <span className="block text-xs text-emerald-600">saves ${r.savings}</span> : null}</td>
                <td className="py-3 pr-4 text-xs text-slate-500">{r.shipping_address}, {r.city}, {r.state} {r.zip}<div>Contact: {r.preferred_contact_method}</div></td>
                <td className="py-3 pr-4 text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString()}</td>
                <td className="py-3 pr-4">
                  <div className="flex items-center gap-2">
                    <span className="rounded-full bg-violet-50 px-2 py-0.5 text-xs font-semibold text-violet-700" data-testid={`equip-status-${r.id}`}>{r.status}</span>
                    <Button size="sm" variant="outline" onClick={() => setManage(r)} data-testid={`equip-manage-btn-${r.id}`}>Manage</Button>
                  </div>
                  {r.tracking_number && <div className="mt-1 text-xs text-slate-400">{r.carrier} {r.tracking_number}</div>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-slate-400">No equipment requests yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <EquipmentManageDialog request={manage} onClose={() => setManage(null)} onSaved={load} />
    </Card>
    </div>
  );
}

function AvailabilityTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/availability").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <Card data-testid="admin-availability">
      <h3 className="text-sm font-bold text-slate-900">Applicant availability</h3>
      <div className="mt-3 space-y-3">
        {rows.map((r) => (
          <div key={r.user_id} className="rounded-xl border border-slate-100 p-3" data-testid={`admin-avail-row-${r.user_id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div><div className="font-medium text-slate-800">{r.name}</div><div className="text-xs text-slate-400">{r.email}</div></div>
              <div className="text-right text-xs text-slate-500">
                <div className="font-semibold text-violet-600">{r.status || "—"}</div>
                <div>{r.preferred_work_type} · {r.preferred_hours_per_week ? `${r.preferred_hours_per_week} hrs/wk` : ""} · {r.timezone}</div>
                <div>Earliest start: {r.earliest_start_date || "—"} · Updated {new Date(r.updated_at).toLocaleDateString()}</div>
              </div>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {DAYS.map((d) => {
                const v = (r.weekly_schedule || {})[d];
                const on = v && v.available;
                return <span key={d} className={`rounded px-2 py-0.5 text-[11px] font-medium ${on ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>{d.slice(0, 3)}{on ? ` ${v.start}-${v.end}` : ""}</span>;
              })}
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="py-8 text-center text-slate-400">No availability saved yet.</div>}
      </div>
    </Card>
  );
}

export default function AdminApplicantData() {
  return (
    <div className="space-y-4" data-testid="admin-applicant-data">
      <Tabs defaultValue="equipment">
        <TabsList><TabsTrigger value="equipment" data-testid="tab-equipment">Equipment Requests</TabsTrigger><TabsTrigger value="availability" data-testid="tab-availability">Availability</TabsTrigger></TabsList>
        <TabsContent value="equipment" className="mt-4"><EquipmentTab /></TabsContent>
        <TabsContent value="availability" className="mt-4"><AvailabilityTab /></TabsContent>
      </Tabs>
    </div>
  );
}
